import React, { useState, useCallback, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { restoreElements } from "@excalidraw/excalidraw";

const WIKI_API = "https://commons.wikimedia.org/w/api.php";

async function searchWikimediaImages(query) {
  const params = new URLSearchParams({
    action: "query",
    generator: "search",
    gsrsearch: `${query} filetype:bitmap`,
    gsrnamespace: "6",
    prop: "imageinfo",
    iiprop: "url|thumburl",
    iiurlwidth: "280",
    format: "json",
    origin: "*",
    gsrlimit: "20",
  });
  const res = await fetch(`${WIKI_API}?${params}`);
  if (!res.ok) throw new Error("Search request failed");
  const data = await res.json();
  const pages = data?.query?.pages || {};
  return Object.values(pages)
    .filter((p) => p.imageinfo?.[0]?.thumburl)
    .map((p) => ({
      id: String(p.pageid),
      title: (p.title || "").replace(/^File:/i, "").replace(/_/g, " "),
      thumbUrl: p.imageinfo[0].thumburl,
      fullUrl: p.imageinfo[0].url,
      mimeType: p.imageinfo[0].mime || "image/jpeg",
    }));
}

async function fetchImageAsDataUrl(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error("Could not fetch image");
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve({ dataUrl: reader.result, mimeType: blob.type || "image/jpeg" });
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * WhiteboardImagePanel
 * Props:
 *   excalidrawAPI   – ref.current from the <Excalidraw /> element
 *   onDragStart     – (url: string) => void  – called when drag begins so
 *                     the parent can track the pending URL for drop handling
 */
const WhiteboardImagePanel = ({ excalidrawAPI, onDragStart, onClose }) => {
  const [query, setQuery] = useState("");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [inserting, setInserting] = useState(null); // image id being inserted
  const debounceRef = useRef(null);

  const doSearch = useCallback(async (q) => {
    if (!q.trim()) {
      setImages([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const results = await searchWikimediaImages(q);
      setImages(results);
      if (results.length === 0) setError(`No images found for "${q}"`);
    } catch {
      setError("Search failed. Check your connection.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 600);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      clearTimeout(debounceRef.current);
      doSearch(query);
    }
  };

  // Insert at the centre of the current viewport
  const insertImageAtCenter = async (img, dropX = null, dropY = null) => {
    if (!excalidrawAPI) return;
    setInserting(img.id);
    try {
      const { dataUrl, mimeType } = await fetchImageAsDataUrl(img.fullUrl);
      const fileId = uuidv4();
      excalidrawAPI.addFiles([
        { id: fileId, dataURL: dataUrl, mimeType, created: Date.now() },
      ]);

      let x, y;
      if (dropX !== null && dropY !== null) {
        x = dropX - 150;
        y = dropY - 100;
      } else {
        const state = excalidrawAPI.getAppState();
        const { scrollX, scrollY, zoom } = state;
        const vpW = state.width ?? window.innerWidth;
        const vpH = state.height ?? window.innerHeight;
        x = (-scrollX + vpW / 2) / zoom.value - 150;
        y = (-scrollY + vpH / 2) / zoom.value - 100;
      }

      const normalized = restoreElements(
        [
          {
            type: "image",
            id: fileId,
            x,
            y,
            width: 300,
            height: 200,
            fileId,
            status: "saved",
            scale: [1, 1],
            angle: 0,
          },
        ],
        null
      );
      excalidrawAPI.updateScene({
        elements: [...excalidrawAPI.getSceneElements(), ...normalized],
      });
    } catch (err) {
      console.error("Image insert failed:", err);
    } finally {
      setInserting(null);
    }
  };

  return (
    <div className="flex flex-col w-64 min-w-[240px] border-r bg-white overflow-hidden h-full">
      {/* Header */}
      <div className="px-3 py-2 bg-indigo-700 flex items-center justify-between">
        <span className="text-white text-sm font-semibold">🖼 Image Search</span>
        <div className="flex items-center gap-2">
          <span className="text-indigo-200 text-[10px]">click or drag to board</span>
          {onClose && (
            <button
              onClick={onClose}
              className="text-indigo-200 hover:text-white text-lg leading-none ml-1"
              title="Close image panel">
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Search input */}
      <div className="p-2 border-b">
        <input
          type="text"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Search images…"
          className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
          autoComplete="off"
        />
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto p-2">
        {loading && (
          <p className="text-xs text-gray-400 text-center mt-6">Searching…</p>
        )}

        {!loading && error && (
          <p className="text-xs text-gray-500 text-center mt-6 px-2">{error}</p>
        )}

        {!loading && !error && images.length === 0 && !query && (
          <p className="text-xs text-gray-400 text-center mt-8 px-3 leading-relaxed">
            Type to search for images.
            <br />
            Click a result to add it to the board, or drag it onto the canvas.
          </p>
        )}

        <div className="grid grid-cols-2 gap-2">
          {images.map((img) => (
            <div
              key={img.id}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData("text/plain", img.fullUrl);
                e.dataTransfer.setData(
                  "application/x-wb-image",
                  JSON.stringify({ url: img.fullUrl, mime: img.mimeType })
                );
                if (onDragStart) onDragStart(img);
              }}
              onClick={() => insertImageAtCenter(img)}
              className={`cursor-pointer rounded overflow-hidden border-2 transition-all
                ${inserting === img.id
                  ? "border-indigo-400 opacity-50"
                  : "border-transparent hover:border-indigo-400 hover:shadow-md"
                }`}
              title={img.title}
            >
              <img
                src={img.thumbUrl}
                alt={img.title}
                className="w-full h-[76px] object-cover bg-gray-100"
                draggable={false}
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Attribution footer */}
      <div className="px-2 py-1.5 border-t bg-gray-50 text-center">
        <a
          href="https://commons.wikimedia.org"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] text-gray-400 hover:text-indigo-500 transition-colors"
        >
          Images from Wikimedia Commons (CC licensed)
        </a>
      </div>
    </div>
  );
};

export { fetchImageAsDataUrl };
export default WhiteboardImagePanel;
